import { inject, Injectable, signal, WritableSignal } from "@angular/core";
import { ModelStoreAbstraction } from "@bitwarden/web-vault/app/services/store/model.store.abstraction";
import { StoreModelNames } from "@bitwarden/web-vault/app/services/dali/type/dali.type";
import { LogService } from "@bitwarden/common/abstractions/log.service";
import {
  ModelCollectionInterface,
  BudgetStoreCollectionType,
  StoreModelCollection,
} from "@bitwarden/web-vault/app/services/store/model.collection.abstraction";
import { toObservable } from "@angular/core/rxjs-interop";
import { WebWorkerQueue } from "@bitwarden/web-vault/app/services/web-worker/WebWorkerQueue";
import { AppStateStoreService } from "@bitwarden/web-vault/app/services/store/app-state/app-state.store.service";
import { TransactionStoreService } from "../transaction/transaction.store.service";
import {
  BehaviorSubject,
  combineLatest,
  distinctUntilChanged,
  filter,
  pairwise,
  skipUntil,
  Subscription,
} from "rxjs";
import { BudgetStoreCollection } from "./budget.store.collection";
import { BudgetDataCalculatedResultCollection } from "@bitwarden/web-vault/app/services/store/budget/budget-data.calculated-result.collection";
import { BudgetTransactionGeneratedCollection } from "@bitwarden/web-vault/app/services/store/budget/budget-transactions.generated.collection";
import { BudgetView } from "@bitwarden/web-vault/app/models/view/budget/budget.view";
import { BudgetUtils } from "@bitwarden/web-vault/app/models/view/budget/budget.utils";
import { BalanceDatesUtils } from "@bitwarden/web-vault/app/services/store/calculation-base/balances/balanceDates.utils";
import { UserStoreService } from "@bitwarden/web-vault/app/services/store/user/user.store.service";
import { SymbolConversion } from "@bitwarden/web-vault/app/services/DataCalculationService/symbol/symbolConversion";
import { CurrencyType } from "@bitwarden/web-vault/app/models/types/location-currency.types";

interface BudgetStoreState {
  status:
    | "Initialise"
    | "Idle"
    | "UpdatingParameters" // updating transactions
    | "AddingCalculation" // calculating a different budget
    | "Error";
}

interface BudgetStorePage {
  isCachedDataDirty: boolean; // transactions updated or budget records updated
  isControlDirty: boolean; // dates filter updated (not implemented yet)
  isParameterDirty?: boolean; // transactions updated
  isBudgetDirty?: boolean; // current budget ID has been updated
}

const StateIsDirty: BudgetStorePage = {
  isCachedDataDirty: true,
  isControlDirty: true,
  isParameterDirty: true,
  isBudgetDirty: true,
};

@Injectable({
  providedIn: "root",
})
export class BudgetStoreService
  extends ModelStoreAbstraction
  implements ModelCollectionInterface<BudgetStoreCollectionType>
{
  /** Services **/
  protected log: LogService = inject(LogService);
  protected transactionsStoreService = inject(TransactionStoreService);
  protected userStoreService = inject(UserStoreService);
  private appState: AppStateStoreService = inject(AppStateStoreService);

  /** Collection  **/
  // Budget collection loaded from DALI
  readonly budget: BudgetStoreCollection = new BudgetStoreCollection();

  // Parameters for the budget calculation
  readonly budgetTransactions = new BudgetTransactionGeneratedCollection();
  // TODO: Add the filter controls here later when we add Date filters to the budget view

  // Calculated Budget Collection
  readonly budgetDataCalculated = new BudgetDataCalculatedResultCollection();

  /** Store internal loading state **/
  protected _storeState$: BehaviorSubject<BudgetStoreState> = new BehaviorSubject({
    status: "Initialise",
  });
  storeState$ = this._storeState$.asObservable();

  /** Synchronous readiness check — "Initialise" is the only status where budgets and enriched
   *  transactions are NOT guaranteed loaded (see onDataCollectionChanged, which is the sole path
   *  out of it). Anything reading budgetViews()/budgetTransactions to derive suggestions or other
   *  cross-collection state should gate on `storeStatus !== "Initialise"`, since budgetViews() is
   *  `null` (not `[]`) during Initialise and right after a clear — callers must handle that with
   *  `?? []`. Don't gate on `=== "Idle"` alone: "Error", "UpdatingParameters", and
   *  "AddingCalculation" are all reached only after the collections have loaded too. */
  get storeStatus(): BudgetStoreState["status"] {
    return this._storeState$.value.status;
  }

  /** Internal Variables **/
  private subscriptions: Subscription[] = [];
  webWorkerQueue = WebWorkerQueue.getInstance();

  // Observables for reacting to changes in collections
  private budgets$ = toObservable(this.budget.budgetViews);
  private preference$ = toObservable(this.userStoreService.preferences.preferenceView);
  private enrichedTransactions$ = toObservable(
    this.transactionsStoreService.enrichTransaction.collection,
  );

  // Budget currencies we've already pulled forex rates for. Only a NEWLY-appearing currency
  // needs a fetch; deletes/renames/amount edits never do. Cleared on core-currency change.
  private ensuredBudgetCurrencies = new Set<string>();

  // Variables used to track the current budget being viewed
  private currentBudgetID = signal<string>(null);
  currentDate = BalanceDatesUtils.getStartOfDayDate(new Date()).getTime();

  constructor() {
    super();
    // Register the category→budget repair hook at construction, NOT in initialize(). This service is
    // a root singleton constructed at bootstrap (injected by StoreLoaderService) before any delete is
    // reachable, and the budget models it repairs are already in memory from the tier-1 vault load —
    // so registering here makes the repair active as soon as the UI unlocks, closing two gaps of the
    // tier-2 `initialize()` timing: the brief window before budget init runs, and the session-long
    // hole where an earlier throw in runTier2Init means `budgetStore.initialize()` never runs at all.
    // Construction happens exactly once for the life of the page (a same-tab logout/login or a vault
    // purge clears store state but never tears down the DI singleton), so no re-registration guard is
    // needed — one constructor, one hook.
    this.registerCategoryRepair();
  }

  initialize(): void {
    this.log.info("Initializing BudgetStoreService");
    this.budget.triggerChanges();
    this.initialiseCollectionListener();
  }

  /**
   * Strip a deleted category's id from every budget's `ctId` at delete time, so a category delete can
   * never leave a budget pointing at a category that no longer exists (#5565). Registered on the
   * categories collection, rather than the categories side referencing the budget collection — this
   * keeps the dependency in the non-circular direction (BudgetStoreService already injects
   * UserStoreService). Called once, from the constructor (see the note there on timing).
   */
  private registerCategoryRepair(): void {
    this.userStoreService.categories.registerReferentialDependent((deletedIds) =>
      this.budget.stripDanglingCategoryRefs(deletedIds),
    );
  }

  protected initialiseCollectionListener(): void {
    this.subscribeToDataChange();
  }

  protected getStorageCollection(modelName: StoreModelNames): StoreModelCollection {
    switch (modelName) {
      case "BudgetStoreModel":
        return this.budget;
      default: {
        this.log.warning(`Need to implement BudgetStoreService ${modelName}`);
      }
    }
  }

  /**
   * Subscribe to changes in the budget collection and enriched transactions
   * @protected
   */
  protected subscribeToDataChange(): void {
    // Subscribe to changes in the budget collection
    const budgetChange$ = this.budgets$.pipe(
      filter((x) => !!x),
      distinctUntilChanged(
        (previous, current) =>
          previous === current || (previous.length === 0 && current.length === 0),
      ),
    );
    this.subscriptions.push(budgetChange$.subscribe(this.onBudgetCollectionChanged.bind(this)));

    const preferenceChange$ = this.preference$.pipe(
      filter((x) => !!x),
      distinctUntilChanged((previous, current) => previous?.coreCurrency === current?.coreCurrency),
      pairwise(),
    );
    this.subscriptions.push(
      preferenceChange$.subscribe(([previous]) =>
        this.onPreferenceChanged(previous.coreCurrency).then(() => {}),
      ),
    );

    // Subscribe to the changes in enrich transactions
    const transactionChange$ = this.enrichedTransactions$.pipe(
      filter((x) => !!x),
      distinctUntilChanged(
        (previous, current) =>
          previous === current || (previous.length === 0 && current.length === 0),
      ),
    );
    this.subscriptions.push(
      transactionChange$.subscribe(this.onEnrichedTransactionsChanged.bind(this)),
    );

    // Only when all the data is loaded we can proceed with the budget calculations by moving
    // the status from Initialize to Idle
    const dataSubscriptions = combineLatest([budgetChange$, transactionChange$]).subscribe(
      this.onDataCollectionChanged.bind(this),
    );

    this.subscriptions.push(dataSubscriptions);
  }

  getBudgetAmountInCoreCurrency(budgetView: BudgetView | null): number {
    const amount = budgetView?.amount ?? 0;
    const coreCurrency = this.userStoreService.preferences.preferenceView()?.coreCurrency;
    const budgetCurrency = budgetView?.currency ?? coreCurrency;

    if (!amount || !coreCurrency || !budgetCurrency || budgetCurrency === coreCurrency) {
      return amount;
    }

    const symbolConversion = new SymbolConversion(
      coreCurrency,
      this.log,
      this.transactionsStoreService.referenceData.referenceDataViews() ?? [],
    );
    return symbolConversion.normalizeQuantity(amount, new Date(), budgetCurrency).normalizedValue;
  }

  /**
   * Build a reusable converter that turns a core-currency amount back into the budget's own currency
   * at a given date. Converting each spend transaction at ITS OWN date (the same date it was
   * normalized to core) makes a same-currency spend round-trip exactly — avoiding the FX drift you'd
   * get from a single today-based factor. Returns identity for legacy/core-currency budgets.
   * ponytail: builds one SymbolConversion up front and reuses it per call — cheap for the handful of
   * transactions on a budget detail; revisit if ever run over the full transaction set.
   */
  getBudgetCurrencyConverter(
    budgetView: BudgetView | null,
  ): (coreAmount: number, date: Date) => number {
    const coreCurrency = this.userStoreService.preferences.preferenceView()?.coreCurrency;
    const budgetCurrency = budgetView?.currency ?? coreCurrency;

    if (!coreCurrency || !budgetCurrency || budgetCurrency === coreCurrency) {
      return (coreAmount) => coreAmount;
    }

    const symbolConversion = new SymbolConversion(
      coreCurrency,
      this.log,
      this.transactionsStoreService.referenceData.referenceDataViews() ?? [],
    );
    // Missing rate makes normalizeQuantity return the core amount unchanged (spend shown in the
    // budget currency but still core-valued). ensureBudgetCurrencyRates prefetches these rates on
    // save/load/currency-change so the gap is transient; this path stays pure display (no fetch).
    return (coreAmount, date) =>
      coreAmount
        ? symbolConversion.normalizeQuantity(coreAmount, date, coreCurrency, budgetCurrency)
            .normalizedValue
        : coreAmount;
  }

  /**
   * Call this function when the user selects a budget from the Manage Budget page
   * This will trigger the store to either load a cached version of the budget calculation
   * or to generate a new budget calculation
   * @param budgetID
   */
  selectBudget(budgetID: string): void {
    this.currentBudgetID.set(budgetID);
    this.refreshState({
      ...StateIsDirty,
      isCachedDataDirty: false,
      isControlDirty: false,
      isParameterDirty: false,
    }).then(() => {});
  }

  /**
   * If the budget collection changes, we need to mark the cached data as dirty and recalculate the current budget
   * @private
   */
  private onBudgetCollectionChanged(): void {
    this.log.info(
      "[BUDGET STORE]: Budget collection changed, marking cached data as dirty and recalculating budget",
    );
    // Instant-fetch-on-save (gloss-client #4053): this handler is driven by
    // budgetChange$ = toObservable(budget.budgetViews), which emits only AFTER the
    // signal has updated. So budget.budgetViews() read inside ensureBudgetCurrencyRates
    // already reflects the just-saved budget — the newly-picked foreign currency is in
    // the list and its rate is fetched now, not deferred to the next initial load.
    void this.ensureBudgetCurrencyRates();
    this.refreshState({
      ...StateIsDirty,
      isControlDirty: false,
      isParameterDirty: false,
    }).then(() => {
      this.log.info("[BUDGET STORE]: Returned from refreshState after budget collection change");
    });
  }

  /**
   * If the enriched transactions change, we need to mark the parameter as dirty, cached data as dirty
   * and recalculate the current budget
   * @private
   */
  private onEnrichedTransactionsChanged(): void {
    this.log.info(
      "[BUDGET STORE]: Enriched transaction collection changed, marking parameter as dirty",
    );
    this.refreshState({
      ...StateIsDirty,
    }).then(() => {
      this.log.info(
        "[BUDGET STORE]: Returned from refreshState after enriched transactions changed",
      );
    });
  }

  private async onPreferenceChanged(previousCoreCurrency: CurrencyType): Promise<void> {
    this.log.info("[BUDGET STORE]: Core currency changed, recalculating budgets");
    // Rates are core-relative; a new core invalidates what we ensured, so allow refetch.
    this.ensuredBudgetCurrencies.clear();
    await this.markLegacyBudgetCurrencies(previousCoreCurrency);
    void this.ensureBudgetCurrencyRates();
    this.refreshState({
      ...StateIsDirty,
      isControlDirty: false,
      isParameterDirty: false,
    }).then(() => {
      this.log.info("[BUDGET STORE]: Returned from refreshState after preference changed");
    });
  }

  /**
   * Pull forex rates for any budget currency that differs from the core currency, so the
   * display converter (getBudgetCurrencyConverter) finds a rate instead of silently falling
   * back to the core amount. Called from async triggers (save/edit, currency change, initial
   * load) — never from a computed/render path. Fire-and-forget: once the rate lands in
   * referenceDataViews the dependent computeds recompute on their own.
   */
  private async ensureBudgetCurrencyRates(): Promise<void> {
    const coreCurrency = this.userStoreService.preferences.preferenceView()?.coreCurrency;
    if (!coreCurrency) {
      return;
    }
    const currencies = (this.budget.budgetViews() ?? [])
      .map((budget) => budget.currency)
      .filter((currency) => currency && currency !== coreCurrency);
    // Only fetch currencies we haven't already ensured — a delete/rename/amount edit adds no new
    // currency, so it must not trigger a forex fetch (recalcDateRange never dedups budget-only
    // currencies, so without this guard every budget change refetches).
    const newCurrencies = currencies.filter(
      (currency) => !this.ensuredBudgetCurrencies.has(currency),
    );
    if (newCurrencies.length === 0) {
      return;
    }
    // Only mark as ensured AFTER a successful fetch — if the forex pull fails, leave the currencies
    // unensured so the next async trigger retries instead of stranding the budget on its core-value
    // fallback until a core-currency change or store reset.
    try {
      const ensured = await this.transactionsStoreService.ensureForexRates(newCurrencies);
      // Mark only on a successful (or no-op) ensure. On a failed pull ensureForexRates
      // returns false, so we leave these currencies unensured and the next async trigger
      // retries — previously the swallowed error let a transient failure mark them ensured
      // and block every in-session retry.
      if (ensured) {
        newCurrencies.forEach((currency) => this.ensuredBudgetCurrencies.add(currency));
      } else {
        this.log.warning(
          `[BUDGET STORE]: forex pull failed; leaving currencies unensured to retry: ${newCurrencies.join(",")}`,
        );
      }
    } catch (e) {
      this.log.warning(`[BUDGET STORE]: Failed to ensure budget currency forex rates: ${e}`);
    }
  }

  private async markLegacyBudgetCurrencies(previousCoreCurrency: CurrencyType): Promise<void> {
    const legacyBudgets = (this.budget.budgetViews() ?? []).filter((budget) => !budget.currency);
    if (!previousCoreCurrency || legacyBudgets.length === 0) {
      return;
    }

    const updatedBudgets = legacyBudgets.map((budget) => {
      const clone = budget.clone();
      clone.currency = previousCoreCurrency;
      return clone;
    });

    await this.budget.saveToVault(updatedBudgets, true);
  }

  /**
   * When all required data collections are loaded, we can move the store state to Idle and trigger
   * a refresh of the current budget calculation
   * @private
   */
  private onDataCollectionChanged(): void {
    if (this._storeState$.value.status === "Initialise") {
      this.log.info("[BUDGET STORE]: All required data loaded, moving store state to Idle");
      this._storeState$.next({ status: "Idle" });
      void this.ensureBudgetCurrencyRates();
      // Trigger a refresh of the state to calculate the budget if needed
      this.refreshState({
        ...StateIsDirty,
      }).then(() => {
        this.log.info(
          "[BUDGET STORE]: Returned from refreshState after data collections initialized",
        );
      });
    }
  }

  /**
   * Refresh the state of the budget store based on the provided page information
   * Will only run if all the required data is loaded and the store is not in Initialise state
   * Will handle clearing cached data, updating parameters which means regenerating the sorted bt category ID
   * transaction collection, and recalculating the budget if needed
   * @param page
   * @private
   */
  private async refreshState(page: BudgetStorePage) {
    /** The budget calculation only runs if the store is no longer initializing **/
    this.log.info("[BUDGET STORE]: refreshState");
    if (this._storeState$.value.status !== "Initialise") {
      if (
        page.isCachedDataDirty ||
        this.currentDate !== BalanceDatesUtils.getStartOfDayDate(new Date()).getTime()
      ) {
        // If the cached data is dirty we need to clear all the cached calculations
        this.log.info("[BUDGET STORE]: Cached data is dirty, clearing budget calculation cache");
        this.budgetDataCalculated.clearCache();
      }

      if (page.isControlDirty) {
        // TODO: implement this in later phase when we have selectable date ranges for budgets
      }

      if (page.isParameterDirty) {
        this._storeState$.next({ status: "UpdatingParameters" });
        // TODO: change this start date later when we have selectable date ranges for budgets
        this.budgetTransactions.generate();
      }

      if (page.isBudgetDirty) {
        if (!this.currentBudgetID()) {
          this.log.info("[BUDGET STORE]: No current ID set, skip budget calculation");
          this.budgetDataCalculated.update(null);
          this._storeState$.next({ status: "Idle" });
          return;
        }
        this.appState.startProcess("budget", "Recalculating Budget");
        this._storeState$.next({ status: "AddingCalculation" });
        // check for a cached version of the budget
        if (this.budgetDataCalculated.cachedCollection()) {
          const cachedBudget = this.budgetDataCalculated
            .cachedCollection()
            .get(this.currentBudgetID());
          if (cachedBudget) {
            this.log.info("[BUDGET STORE]: Returning cached version of budget calculation");
            this.budgetDataCalculated.update(cachedBudget);
            this._storeState$.next({ status: "Idle" });
            this.appState.endProcess("budget");
            return;
          }
        }
        this.log.info(
          "[BUDGET STORE]: No cached version of budget calculation found, generating new calculation",
        );
        // check that the budget exists
        // budgetViews() is null-initialised and reset to null on clear; guard like the
        // sibling call above so a store clear mid-calculation can't throw on .find (#3943).
        const budgetView = (this.budget.budgetViews() ?? []).find(
          (budgetView) => budgetView.id === this.currentBudgetID(),
        );
        if (!budgetView) {
          this._storeState$.next({ status: "Error" });
          this.appState.endProcess("budget");
          throw new Error(
            `[BUDGET STORE]: Budget with ID ${this.currentBudgetID()} does not exist in the store`,
          );
        }

        const calculatingBudgetID = this.currentBudgetID();
        // if no cached version, generate a new budget calculation
        this.calculateBudget(budgetView).then(() => {
          this.log.info(
            `[BUDGET STORE]: Budget calculation for budget ID ${calculatingBudgetID} complete`,
          );
          // Don't stomp a clearStore() that landed while this calculation was in flight —
          // it already reset status to "Initialise" and only onDataCollectionChanged is
          // allowed to move it back to "Idle".
          if (this._storeState$.value.status !== "Initialise") {
            this._storeState$.next({ status: "Idle" });
          }
        });
      }
    } else {
      this.log.info("[BUDGET STORE]: Store is still initializing, not refreshing state");
    }
  }

  private async calculateBudget(budgetView: BudgetView): Promise<void> {
    this.log.info("[BUDGET STORE]: Calculating fresh budget ...");
    const convertedBudget = budgetView.clone();
    convertedBudget.amount = this.getBudgetAmountInCoreCurrency(budgetView);

    await BudgetUtils.calculateBudgetResults(
      convertedBudget,
      this.webWorkerQueue,
      this.budgetTransactions.sortedCollection(),
    ).then((budgetResult) => {
      this.log.info("[BUDGET STORE]: Budget calculation completed");
      if (this.currentBudgetID() === budgetView.id) {
        // Current budget ID has not changed during the calculation, we can update the store
        this.log.info(
          "[BUDGET STORE]: BudgetID still matches current selection, updating result in store",
        );
        this.budgetDataCalculated.update(budgetResult);
        this.budgetDataCalculated.updateCache(budgetView.id, budgetResult);
        this.appState.endProcess("budget");
      } else {
        // Current budget ID has changed during the calculation, only update the cache
        this.budgetDataCalculated.updateCache(budgetView.id, budgetResult);
        this.log.info(
          "[BUDGET STORE]: BudgetID no longer matches current selection, not updating store with the result, " +
            "only saving to cache",
        );
        this.appState.endProcess("budget");
      }
    });
  }

  clearStore(): void {
    this.log.info("[BUDGET STORE]: Clearing BudgetStoreService");
    this.subscriptions.forEach((s) => {
      s.unsubscribe();
    });
    this.currentBudgetID.set(null);
    this.ensuredBudgetCurrencies.clear();
    this.subscriptions = [];
    this.budget.clear();
    this.budgetDataCalculated.clear();
    this.budgetTransactions.clear();
    // Reset so a same-tab logout -> different-account login doesn't leave storeStatus-gated
    // readers (e.g. AutoBudgetService) seeing "Idle" against these now-empty collections.
    this._storeState$.next({ status: "Initialise" });
  }
}
